import { LOWER_FDI, UPPER_FDI, toothLabel } from '@odo/shared-ui/clinical/elastics/toothGeometry';

/** One tooth as DM stores it (ISO/FDI) and as the grid shows it (US Universal). */
export interface GridTooth {
    iso: string;
    label: string;
}

// Primary teeth, patient's right to left as drawn: Universal A-J upper, T-K lower.
const UPPER_PRIMARY_FDI = [55, 54, 53, 52, 51, 61, 62, 63, 64, 65] as const;
const LOWER_PRIMARY_FDI = [85, 84, 83, 82, 81, 71, 72, 73, 74, 75] as const;
const UPPER_PRIMARY_LETTERS = 'ABCDEFGHIJ';
const LOWER_PRIMARY_LETTERS = 'TSRQPONMLK';

function permanent(fdi: number): GridTooth {
    return { iso: String(fdi), label: toothLabel(fdi) };
}

function primary(fdi: readonly number[], letters: string): GridTooth[] {
    return fdi.map((tooth, index) => ({ iso: String(tooth), label: letters[index] }));
}

export const UPPER_PERMANENT: readonly GridTooth[] = UPPER_FDI.map(permanent);
export const UPPER_PRIMARY: readonly GridTooth[] = primary(
    UPPER_PRIMARY_FDI,
    UPPER_PRIMARY_LETTERS
);
export const LOWER_PRIMARY: readonly GridTooth[] = primary(
    LOWER_PRIMARY_FDI,
    LOWER_PRIMARY_LETTERS
);
export const LOWER_PERMANENT: readonly GridTooth[] = LOWER_FDI.map(permanent);

const ALL_TEETH = [...UPPER_PERMANENT, ...UPPER_PRIMARY, ...LOWER_PRIMARY, ...LOWER_PERMANENT];
const LABEL_BY_ISO = new Map(ALL_TEETH.map((tooth) => [tooth.iso, tooth.label]));
const PRIMARY_RANK_OFFSET = 100;
const UNKNOWN_RANK = 999;

// Universal order: permanent 1-32, then primary A-T.
function rank(iso: string): number {
    const label = LABEL_BY_ISO.get(iso);
    if (!label) return UNKNOWN_RANK;
    const number = Number(label);
    return Number.isNaN(number) ? PRIMARY_RANK_OFFSET + label.charCodeAt(0) : number;
}

export function sortTeeth(teeth: readonly string[]): string[] {
    return [...teeth].sort((a, b) => rank(a) - rank(b));
}

/** "3, 4, 14, A" for display; unknown codes pass through as DM sent them. */
export function formatTeeth(teeth: readonly string[]): string {
    return sortTeeth(teeth)
        .map((iso) => LABEL_BY_ISO.get(iso) ?? iso)
        .join(', ');
}
